import {
  FloatingPortal,
  useMergeRefs,
} from "@floating-ui/react"
import type {
  RefObject,
  ForwardedRef,
} from "react"
import {
  forwardRef,
  useLayoutEffect,
} from "react"

import { useGetMgdlAsTopPosition } from "../useGetMgdlAsTopPosition"
import { TooltipContent } from "./TooltipContent"
import { useGetTimeAsXPosition } from "./useGetTimeAsXPosition"
import { useTooltipContext } from "./useTooltipContext"

type Props = React.HTMLProps<HTMLDivElement> & {
  containerRef: RefObject<HTMLDivElement | null>; // reference to the element that contains the graph
  containerBoundingClientRect: DOMRect; // the bounding client rect of the graph container
}

/*
 * this portal renders the tooltip content in a different place in the DOM tree than where the
 * tooltip trigger is
 */
export const TooltipPortal = forwardRef<HTMLDivElement, Props>(
  function TooltipPortal(props: Props, propRef: ForwardedRef<HTMLDivElement>) {
    const {
      containerRef,
      containerBoundingClientRect,
      ...htmlProps
    } = props

    const {
      reading,
      refs,
      strategy,
      x,
      y,
      open: isOpen,
      getFloatingProps,
    } = useTooltipContext()

    const getTimeAsXPosition = useGetTimeAsXPosition()
    const getMgdlAsTopPosition = useGetMgdlAsTopPosition()

    const xPosition = reading === undefined
      ? undefined
      : getTimeAsXPosition(reading.startedAt)

    const [yPosition] = reading === undefined
      ? [undefined]
      : getMgdlAsTopPosition(reading?.glucose.mg_dl)

    const stateRefs = refs

    useLayoutEffect(
      () => {
        if (yPosition === undefined || xPosition === undefined) {
          return
        }

        if (!containerBoundingClientRect.width) {
          return
        }

        if (!reading) {
          return
        }

        stateRefs.setReference({
          getBoundingClientRect: () => ({
            width: 0,
            height: 0,
            x: containerBoundingClientRect.x + xPosition,
            y: containerBoundingClientRect.y + yPosition,
            left: containerBoundingClientRect.left + xPosition,
            right: containerBoundingClientRect.right + xPosition,
            top: containerBoundingClientRect.top + yPosition,
            bottom: containerBoundingClientRect.bottom + yPosition,
          }),
        })
      },
      [
        reading,
        stateRefs,
        containerBoundingClientRect,
        xPosition,
        yPosition,
      ],
    )

    const ref = useMergeRefs<HTMLDivElement>([
      refs.setFloating,
      propRef,
    ])

    return (
      <FloatingPortal root={containerRef}>
        {reading && (
          <TooltipContent
            reading={reading}
            ref={ref}
            style={{
              position: strategy,
              top: 0,
              left: 0, // TODO rtl ?
              // this element is positioned by transform
              transform: `translate(${x}px, ${y}px)`,
              visibility: isOpen ? "visible" : "hidden",
              ...htmlProps.style,
            }}
            {...getFloatingProps(htmlProps)}
          />
        )}
      </FloatingPortal>
    )
  },
)
